import { Node } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Redo2,
  SeparatorHorizontal,
  Table2,
  Underline,
  Undo2,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import type { ClipboardEvent } from 'react';

const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  parseHTML: () => [{ tag: 'div[data-page-break]' }],
  renderHTML: () => ['div', { 'data-page-break': 'true' }],
});

export interface RichClinicalEditorHandle {
  focus: () => void;
  getHTML: () => string;
  insertContent: (html: string) => void;
  setContent: (html: string) => void;
}

interface RichClinicalEditorProps {
  value: string;
  editable: boolean;
  allowImages?: boolean;
  compact?: boolean;
  onChange: (html: string) => void;
  onImageRejected?: () => void;
}

export const RichClinicalEditor = forwardRef<
  RichClinicalEditorHandle,
  RichClinicalEditorProps
>(function RichClinicalEditor(
  {
    value,
    editable,
    allowImages = true,
    compact = false,
    onChange,
    onImageRejected,
  },
  ref,
) {
  const savedSelectionRef = useRef<number | null>(null);
  const editor = useEditor({
    immediatelyRender: false,
    content: value,
    editable,
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TableKit.configure({ table: { resizable: true } }),
      Image.configure({
        allowBase64: true,
        resize: {
          enabled: true,
          directions: ['bottom-right'],
          minWidth: 80,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      PageBreak,
    ],
    editorProps: {
      attributes: {
        class: `rich-editor${compact ? ' rich-editor-compact' : ''}`,
        'aria-label': 'Editor clínico enriquecido',
      },
    },
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  });

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  useEffect(() => {
    if (!editor || editor.getHTML() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus(),
      getHTML: () => editor?.getHTML() ?? value,
      insertContent: (html) => {
        if (!editor) return;
        const normalized = /^\s*<(p|h[1-6]|ul|ol|table|blockquote|div|img|hr)\b/i.test(html)
          ? html
          : `<p>${html}</p>`;
        if (savedSelectionRef.current !== null) {
          editor
            .chain()
            .setTextSelection(savedSelectionRef.current)
            .insertContent(normalized)
            .focus()
            .run();
          savedSelectionRef.current = editor.state.selection.from;
          return;
        }
        const selection = window.getSelection();
        const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
        if (range && editor.view.dom.contains(range.startContainer)) {
          const position = editor.view.posAtDOM(
            range.startContainer,
            range.startOffset,
          );
          editor.chain().setTextSelection(position).insertContent(normalized).focus().run();
          return;
        }
        editor.chain().insertContent(normalized).focus().run();
      },
      setContent: (html) => {
        editor?.commands.setContent(html, { emitUpdate: false });
      },
    }),
    [editor, value],
  );

  const pasteImage = (event: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.files ?? []);
    const imageItem = Array.from(event.clipboardData.items ?? []).find((item) =>
      item.type.startsWith('image/'),
    );
    if (!allowImages && imageItem) {
      event.preventDefault();
      event.stopPropagation();
      onImageRejected?.();
      return;
    }
    const itemFile = imageItem?.getAsFile?.();
    const image = files.find((file) => file.type.startsWith('image/')) ?? itemFile;
    if (!image) return;
    event.preventDefault();
    event.stopPropagation();
    if (!allowImages) {
      onImageRejected?.();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      editor
        ?.chain()
        .focus()
        .setImage({ src: String(reader.result), alt: image.name })
        .run();
    };
    reader.readAsDataURL(image);
  };

  const preserveNativeSelection = () => {
    if (!editor) return;
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !editor.view.dom.contains(range.startContainer)) return;
    let position = editor.view.posAtDOM(range.startContainer, range.startOffset);
    if (position <= 0) {
      const prefix = document.createRange();
      prefix.selectNodeContents(editor.view.dom);
      prefix.setEnd(range.startContainer, range.startOffset);
      let remaining = prefix.toString().length;
      editor.state.doc.descendants((node, nodePosition) => {
        if (!node.isText) return true;
        const length = node.text?.length ?? 0;
        if (remaining <= length) {
          position = nodePosition + remaining;
          return false;
        }
        remaining -= length;
        return true;
      });
    }
    savedSelectionRef.current = position;
    editor.commands.setTextSelection(position);
  };

  const toolbarButton = (
    label: string,
    icon: React.ReactNode,
    action: () => void,
    active = false,
  ) => (
    <button
      type="button"
      disabled={!editable}
      className={active ? 'active' : ''}
      onClick={action}
      title={label}
      aria-label={label}
    >
      {icon}
    </button>
  );

  return (
    <div
      className="rich-editor-shell"
      onPasteCapture={pasteImage}
      onInput={(event) => {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('rich-editor')) return;
        if (!editor || editor.getHTML() === target.innerHTML) return;
        editor.commands.setContent(target.innerHTML, { emitUpdate: true });
      }}
      onMouseUp={preserveNativeSelection}
      onKeyUp={preserveNativeSelection}
    >
      <div className="rich-editor-toolbar" aria-label="Herramientas de edición">
        {toolbarButton('Negrita', <Bold size={17} />, () => editor?.chain().focus().toggleBold().run(), editor?.isActive('bold'))}
        {toolbarButton('Cursiva', <Italic size={17} />, () => editor?.chain().focus().toggleItalic().run(), editor?.isActive('italic'))}
        {toolbarButton('Subrayado', <Underline size={17} />, () => editor?.chain().focus().toggleUnderline().run(), editor?.isActive('underline'))}
        {toolbarButton('Título', <Heading2 size={17} />, () => editor?.chain().focus().toggleHeading({ level: 2 }).run(), editor?.isActive('heading', { level: 2 }))}
        {toolbarButton('Lista', <List size={17} />, () => editor?.chain().focus().toggleBulletList().run(), editor?.isActive('bulletList'))}
        {toolbarButton('Lista numerada', <ListOrdered size={17} />, () => editor?.chain().focus().toggleOrderedList().run(), editor?.isActive('orderedList'))}
        {toolbarButton('Alinear a la izquierda', <AlignLeft size={17} />, () => editor?.chain().focus().setTextAlign('left').run(), editor?.isActive({ textAlign: 'left' }))}
        {toolbarButton('Centrar', <AlignCenter size={17} />, () => editor?.chain().focus().setTextAlign('center').run(), editor?.isActive({ textAlign: 'center' }))}
        {toolbarButton('Alinear a la derecha', <AlignRight size={17} />, () => editor?.chain().focus().setTextAlign('right').run(), editor?.isActive({ textAlign: 'right' }))}
        {toolbarButton('Insertar tabla', <Table2 size={17} />, () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}
        {allowImages && toolbarButton('Pegar imagen', <ImagePlus size={17} />, () => editor?.commands.focus())}
        {toolbarButton('Salto de página', <SeparatorHorizontal size={17} />, () => editor?.chain().focus().insertContent({ type: 'pageBreak' }).run())}
        {toolbarButton('Deshacer', <Undo2 size={17} />, () => editor?.chain().focus().undo().run())}
        {toolbarButton('Rehacer', <Redo2 size={17} />, () => editor?.chain().focus().redo().run())}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});
